import { motion } from 'framer-motion';
import {
  DollarSign,
  Building2,
  Users,
  ArrowRight,
  Phone,
  CheckCircle2,
  Landmark,
  Briefcase,
  FileText,
  Target,
  ChevronDown,
  Handshake,
  Wallet,
  Upload,
  Loader2
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';
import { NdaModal } from '../components/NdaModal';
import { useState } from 'react';

const fundingSources = [
  {
    icon: Landmark,
    title: 'Bank & Leasing',
    description: 'Akses ke produk pinjaman bisnis, KUR, dan fasilitas leasing'
  },
  {
    icon: Handshake,
    title: 'Angel Investor',
    description: 'Koneksi ke investor individu untuk seed funding'
  },
  {
    icon: Briefcase,
    title: 'Venture Capital',
    description: 'Akses ke VC untuk scale-up dan growth funding'
  },
  {
    icon: Wallet,
    title: 'Alternative Financing',
    description: 'Crowdfunding, P2P lending, dan skema pendanaan kreatif'
  }
];

const howItWorks = [
  {
    number: '01',
    title: 'Apply',
    description: 'Isi form aplikasi dengan data bisnis Anda'
  },
  {
    number: '02',
    title: 'Review',
    description: 'Tim kami menilai kelayakan dan bankability'
  },
  {
    number: '03',
    title: 'Match',
    description: 'Kami mencarikan investor/pendanaan yang cocok'
  },
  {
    number: '04',
    title: 'Funded',
    description: 'Dana cair dan bisnis Anda siap berkembang'
  }
];

const benefits = [
  {
    icon: Target,
    title: 'Targeted Matching',
    description: 'Kami carikan investor yang sesuai dengan industri dan stage bisnis Anda'
  },
  {
    icon: FileText,
    title: 'Dokumen Investasi',
    description: 'Bantu menyiapkan proposal, pitch deck, dan financial projection'
  },
  {
    icon: Building2,
    title: 'Bankable Structure',
    description: 'Rapikan struktur keuangan dan legal agar investable'
  },
  {
    icon: DollarSign,
    title: 'Multiple Sources',
    description: 'Akses ke berbagai jalur pendanaan, tidak hanya satu opsi'
  }
];

const faqs = [
  {
    question: 'Berapa lama proses matching investor?',
    answer: 'Tergantung kesiapan dokumen dan kelayakan bisnis. Rata-rata 2-4 minggu untuk menemukan investor yang cocok.'
  },
  {
    question: 'Apa saja dokumen yang diperlukan?',
    answer: 'Business plan, financial statements (jika ada), legal documents (NIB, NPWP, dll), dan company profile.'
  },
  {
    question: 'Ada biaya upfront?',
    answer: 'Kami tidak mengenakan biaya upfront. Fee kami adalah success fee setelah funding berhasil didapatkan.'
  },
  {
    question: 'Bisnis apapun bisa apply?',
    answer: 'Kami fokus pada bisnis dengan skalabilitas dan proven business model. Startup, UMKM growth, dan perusahaan established semua bisa apply.'
  }
];

export function Funding() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: processRef, isInView: processInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isInView: benefitsInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isInView: faqInView } = useScrollAnimation({ threshold: 0.1 });
  
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showNdaModal, setShowNdaModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    companyName: '',
    industry: '',
    businessStage: '',
    fundingAmount: '',
    fundingPurpose: '',
    employeeCount: '',
    estimatedRevenue: '',
    businessDescription: '',
    annualRevenue: '',
    hasDocuments: false,
    agreeToTerms: false,
    companyProfile: null as File | null,
    financialReport: null as File | null,
    businessPlan: null as File | null,
    pitchDeck: null as File | null,
    otherDocuments: null as File | null
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    
    // Validate NDA agreement
    if (!formData.agreeToTerms) {
      setErrorMessage('Anda harus menyetujui syarat dan ketentuan NDA untuk melanjutkan.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    // Validate Company Profile upload
    if (!formData.companyProfile) {
      setErrorMessage('Company Profile wajib diunggah.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    // Validate Financial Report upload
    if (!formData.financialReport) {
      setErrorMessage('Laporan Keuangan (3 tahun terakhir) wajib diunggah.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    // Validate required text fields
    if (!formData.fullName || !formData.email || !formData.phone || !formData.position ||
        !formData.companyName || !formData.industry || !formData.businessStage ||
        !formData.fundingAmount || !formData.fundingPurpose || !formData.employeeCount ||
        !formData.estimatedRevenue || !formData.annualRevenue || !formData.businessDescription) {
      setErrorMessage('Semua field wajib diisi.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    // Validate Business Plan & Pitch Deck if checkbox is checked
    if (formData.hasDocuments) {
      if (!formData.businessPlan) {
        setErrorMessage('Business Plan wajib diunggah karena Anda mencentang opsi dokumen.');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
      if (!formData.pitchDeck) {
        setErrorMessage('Pitch Deck wajib diunggah karena Anda mencentang opsi dokumen.');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Validate Business Description minimum 100 characters
    if (!formData.businessDescription || formData.businessDescription.length < 100) {
      setErrorMessage('Deskripsi Bisnis wajib diisi minimal 100 karakter.');
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Use production backend API
      const API_URL = 'https://empbiz-backend.onrender.com/api/funding';
      
      // Create FormData for backend API
      const formDataToSend = new FormData();
      
      // Add all text fields
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('companyName', formData.companyName);
      formDataToSend.append('industry', formData.industry);
      formDataToSend.append('businessStage', formData.businessStage);
      formDataToSend.append('fundingAmount', formData.fundingAmount);
      formDataToSend.append('fundingPurpose', formData.fundingPurpose);
      formDataToSend.append('employeeCount', formData.employeeCount);
      formDataToSend.append('estimatedRevenue', formData.estimatedRevenue);
      formDataToSend.append('annualRevenue', formData.annualRevenue);
      formDataToSend.append('businessDescription', formData.businessDescription);
      formDataToSend.append('hasDocuments', formData.hasDocuments ? 'Ya' : 'Tidak');
      
      // Add files
      if (formData.companyProfile) {
        formDataToSend.append('companyProfile', formData.companyProfile);
      }
      
      if (formData.financialReport) {
        formDataToSend.append('financialReport', formData.financialReport);
      }
      
      if (formData.businessPlan) {
        formDataToSend.append('businessPlan', formData.businessPlan);
      }
      
      if (formData.pitchDeck) {
        formDataToSend.append('pitchDeck', formData.pitchDeck);
      }
      
      if (formData.otherDocuments) {
        formDataToSend.append('otherDocuments', formData.otherDocuments);
      }

      // Send to backend API
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formDataToSend
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitStatus('success');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            position: '',
            companyName: '',
            industry: '',
            businessStage: '',
            fundingAmount: '',
            fundingPurpose: '',
            employeeCount: '',
            estimatedRevenue: '',
            businessDescription: '',
            annualRevenue: '',
            hasDocuments: false,
            agreeToTerms: false,
            companyProfile: null,
            financialReport: null,
            businessPlan: null,
            pitchDeck: null,
            otherDocuments: null
          });
          setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
      
    } catch (error) {
      console.error('Backend Error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Terjadi kesalahan saat mengirim formulir. Silakan coba lagi.');
      setErrorMessage('Terjadi kesalahan saat mengirim formulir. Silakan coba lagi.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/funding-page-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />
        
        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">10</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Funding Advisory & Channeling Service
            </h1>

            <p className="text-xl text-white/70 mb-4 leading-relaxed">
              <strong className="text-primary-400">Pendanaan Bisnis</strong> untuk Pertumbuhan Berkelanjutan
            </p>

            <p className="text-white/60 mb-8">
              Kami bantu Anda mendapatkan akses pendanaan yang tepat (bank, investor, maupun alternatif financing) sekaligus menyiapkan struktur keuangan agar lebih <span className="whitespace-nowrap">bankable dan&nbsp;investable</span>.
            </p>

            <motion.a
              href="#apply"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <DollarSign className="w-5 h-5" />
              Ajukan Pendanaan
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Combined Section: Funding Sources + How It Works */}
      <section ref={processRef} className="py-20 bg-dark-800/30">
        <div className="max-w-7xl mx-auto section-padding">
          
          {/* Funding Sources */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Akses ke Berbagai Sumber Pendanaan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Kami membuka akses ke berbagai jalur pendanaan sesuai kebutuhan bisnis Anda
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {fundingSources.map((source, index) => (
              <motion.div
                key={source.title}
                initial={{ opacity: 0, y: 20 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300 text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-primary-600/20 flex items-center justify-center mx-auto mb-4">
                  <source.icon className="w-7 h-7 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{source.title}</h3>
                <p className="text-white/60 text-sm">{source.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-white/10 mb-20" />

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Cara Kerja
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Proses simpel dari aplikasi hingga funding
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                className="relative"
              >
                <div className="glass rounded-2xl p-6 text-center">
                  <span className="text-4xl font-display font-bold text-primary-500/30 block mb-4">
                    {step.number}
                  </span>
                  <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm">{step.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-primary-500/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="relative py-20 overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/funding-benefits-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />
        
        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Mengapa Memilih Kami?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Keunggulan layanan funding advisory kami
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-white/60 text-sm">{benefit.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Terms & Conditions Section */}
      <section className="py-20 bg-dark-800/30">
        <div className="max-w-4xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Syarat & Ketentuan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Ketentuan yang perlu dipenuhi untuk mengajukan pendanaan
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="glass rounded-2xl p-8"
          >
            <div className="space-y-6">
              {/* Persyaratan Dasar */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-400" />
                  Persyaratan Dasar
                </h3>
                <ul className="space-y-3 text-white/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Badan usaha yang telah terdaftar (PT, CV, atau bentuk legal lainnya)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>NIB (Nomor Induk Berusaha) yang masih berlaku</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>NPWP perusahaan dan pemegang saham/pengurus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Memiliki rekening bank atas nama perusahaan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Laporan keuangan minimal 3 tahun (untuk perusahaan yang sudah beroperasi)</span>
                  </li>
                </ul>
              </div>

              {/* Ketentuan Proses */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary-400" />
                  Ketentuan Proses
                </h3>
                <ul className="space-y-3 text-white/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Success fee dikenakan setelah funding berhasil didapatkan (2-5% dari total funding)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Biaya analisis menyeluruh perusahaan dikenakan setelah seluruh dokumentasi diterima (berlaku untuk due diligence mendalam)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Kerahasiaan data dijamin melalui NDA (Non-Disclosure Agreement)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Perusahaan wajib memberikan informasi yang akurat dan lengkap</span>
                  </li>
                </ul>
              </div>

              {/* Ketentuan Tambahan */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-400" />
                  Ketentuan Tambahan
                </h3>
                <ul className="space-y-3 text-white/70">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Perusahaan tidak sedang dalam proses pailit atau likuidasi</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Tidak memiliki catatan kredit macet (tidak berlaku untuk startup early stage)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Bersedia melakukan due diligence dan verifikasi data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span>Bersedia memberikan akses dokumen legal dan finansial yang diperlukan</span>
                  </li>
                </ul>
              </div>

              {/* Catatan */}
              <div className="mt-8 p-4 bg-primary-600/10 rounded-lg border border-primary-500/20">
                <p className="text-white/70 text-sm">
                  <strong className="text-primary-400">Catatan:</strong> Keberhasilan pendanaan tergantung pada kelayakan bisnis,
                  kesiapan dokumen, dan kesesuaian dengan kriteria investor/pendanaan. Emporium Solution berhak menolak aplikasi
                  yang tidak memenuhi persyaratan minimum.
                </p>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-white/10 my-8" />

              {/* FAQ Section */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <ChevronDown className="w-5 h-5 text-primary-400" />
                  Pertanyaan Umum
                </h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="glass rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left"
                      >
                        <span className="font-semibold text-white">{faq.question}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-primary-400 transition-transform ${
                            openFaq === index ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaq === index && (
                        <div className="px-6 pb-4">
                          <p className="text-white/60">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply" className="relative py-20 overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/funding-form-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />
        
        <div className="relative max-w-4xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Ajukan Pendanaan
            </h2>
            <p className="text-white/60">
              Isi form di bawah dan tim kami akan menghubungi Anda
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="glass rounded-2xl p-8"
          >
            {/* Personal Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-400" />
                Informasi Personal
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nama Lengkap <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Email <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nomor Telepon <span className="text-red-400">*</span></label>
                  <input
                    type="tel"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="0812-3456-7890"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Jabatan <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Founder / CEO"
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Business Information */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary-400" />
                Informasi Bisnis
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Nama Perusahaan <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="PT Example Indonesia"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Industri <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Technology / F&B / Manufacturing"
                    value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Tahap Bisnis <span className="text-red-400">*</span></label>
                  <select
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary-500"
                    value={formData.businessStage}
                    onChange={(e) => setFormData({...formData, businessStage: e.target.value})}
                  >
                    <option value="" className="bg-dark-800">Pilih Tahap</option>
                    <option value="ideation" className="bg-dark-800">Ideation / Concept</option>
                    <option value="mvp" className="bg-dark-800">MVP / Early Stage</option>
                    <option value="growth" className="bg-dark-800">Growth / Scale-up</option>
                    <option value="established" className="bg-dark-800">Established</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Jumlah Pendanaan yang Dibutuhkan <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Rp 500 Juta - 2 Miliar"
                    value={formData.fundingAmount}
                    onChange={(e) => setFormData({...formData, fundingAmount: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Jumlah Karyawan <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Contoh: 10-50 orang"
                    value={formData.employeeCount}
                    onChange={(e) => setFormData({...formData, employeeCount: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Estimasi Omset per Tahun <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Rp 1 Miliar - 5 Miliar"
                    value={formData.estimatedRevenue}
                    onChange={(e) => setFormData({...formData, estimatedRevenue: e.target.value})}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-2">Penggunaan Dana <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Ekspansi, Working Capital, R&D, dll"
                    value={formData.fundingPurpose}
                    onChange={(e) => setFormData({...formData, fundingPurpose: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Estimasi Profit Tahunan (%) <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500"
                    placeholder="Contoh: 10% - 20%"
                    value={formData.annualRevenue}
                    onChange={(e) => setFormData({...formData, annualRevenue: e.target.value})}
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <label className="block text-white/70 text-sm mb-2 opacity-0">Dokumen</label>
                  <div className="flex items-center h-[46px]">
                    <input
                      type="checkbox"
                      id="documents"
                      className="w-6 h-6 rounded border-white/20 bg-white/5 text-primary-600 focus:ring-primary-500 cursor-pointer"
                      checked={formData.hasDocuments}
                      onChange={(e) => setFormData({...formData, hasDocuments: e.target.checked})}
                    />
                    <label htmlFor="documents" className="ml-3 text-white/70 text-sm cursor-pointer select-none">
                      Saya sudah memiliki dokumen Business Plan dan Pitch Deck
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Profile Upload - Always Required */}
            <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-xl border-l-4 border-l-primary-500">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-400" />
                Company Profile <span className="text-red-400 text-sm">*</span>
              </h4>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf"
                  required
                  onChange={(e) => setFormData({...formData, companyProfile: e.target.files?.[0] || null})}
                  className="hidden"
                  id="companyProfile"
                />
                <label
                  htmlFor="companyProfile"
                  className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 border-dashed rounded-lg cursor-pointer hover:bg-white/10 hover:border-primary-500/50 transition-all"
                >
                  <Upload className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span className="text-white/60 text-sm truncate">
                    {formData.companyProfile ? formData.companyProfile.name : 'Pilih file PDF (wajib)...'}
                  </span>
                </label>
              </div>
              <p className="text-white/40 text-xs mt-2">
                Dokumen wajib: Profil perusahaan lengkap dengan informasi legalitas dan struktur organisasi
              </p>
            </div>

            {/* Financial Report Upload - Always Required */}
            <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-xl border-l-4 border-l-primary-500">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-400" />
                Laporan Keuangan (3 tahun terakhir) <span className="text-red-400 text-sm">*</span>
              </h4>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf"
                  required
                  onChange={(e) => setFormData({...formData, financialReport: e.target.files?.[0] || null})}
                  className="hidden"
                  id="financialReport"
                />
                <label
                  htmlFor="financialReport"
                  className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 border-dashed rounded-lg cursor-pointer hover:bg-white/10 hover:border-primary-500/50 transition-all"
                >
                  <Upload className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span className="text-white/60 text-sm truncate">
                    {formData.financialReport ? formData.financialReport.name : 'Pilih file PDF (wajib)...'}
                  </span>
                </label>
              </div>
              <p className="text-white/40 text-xs mt-2">
                Dokumen wajib: Laporan keuangan audit 3 tahun terakhir (jika ada). Untuk startup early stage, dapat menyertakan proyeksi keuangan.
              </p>
            </div>

            {/* Other Documents Upload - Always Available */}
            <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-xl">
              <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-400" />
                Dokumen Lainnya
              </h4>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormData({...formData, otherDocuments: e.target.files?.[0] || null})}
                  className="hidden"
                  id="otherDocumentsAlways"
                />
                <label
                  htmlFor="otherDocumentsAlways"
                  className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 border-dashed rounded-lg cursor-pointer hover:bg-white/10 hover:border-primary-500/50 transition-all"
                >
                  <Upload className="w-5 h-5 text-primary-400 flex-shrink-0" />
                  <span className="text-white/60 text-sm truncate">
                    {formData.otherDocuments ? formData.otherDocuments.name : 'Pilih file PDF...'}
                  </span>
                </label>
              </div>
              <p className="text-white/40 text-xs mt-2">
                NPWP Perusahaan, KTP Direksi, Bukti Kepemilikan Aset, atau dokumen pendukung lainnya
              </p>
            </div>

            {/* Business Plan & Pitch Deck - Shows when checkbox is checked */}
            {formData.hasDocuments && (
              <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-xl">
                <h4 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary-400" />
                  Dokumen Pendukung <span className="text-red-400 text-sm">* (Wajib)</span>
                </h4>
                <div className="space-y-6">
                  {/* Business Plan Upload */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Business Plan (PDF) <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf"
                        required
                        onChange={(e) => setFormData({...formData, businessPlan: e.target.files?.[0] || null})}
                        className="hidden"
                        id="businessPlan"
                      />
                      <label
                        htmlFor="businessPlan"
                        className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 border-dashed rounded-lg cursor-pointer hover:bg-white/10 hover:border-primary-500/50 transition-all"
                      >
                        <FileText className="w-5 h-5 text-primary-400 flex-shrink-0" />
                        <span className="text-white/60 text-sm truncate">
                          {formData.businessPlan ? formData.businessPlan.name : 'Pilih file PDF (wajib)...'}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Pitch Deck Upload */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Pitch Deck (PDF) <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf"
                        required
                        onChange={(e) => setFormData({...formData, pitchDeck: e.target.files?.[0] || null})}
                        className="hidden"
                        id="pitchDeck"
                      />
                      <label
                        htmlFor="pitchDeck"
                        className="flex items-center gap-3 px-4 py-3 bg-white/5 border border-white/10 border-dashed rounded-lg cursor-pointer hover:bg-white/10 hover:border-primary-500/50 transition-all"
                      >
                        <FileText className="w-5 h-5 text-primary-400 flex-shrink-0" />
                        <span className="text-white/60 text-sm truncate">
                          {formData.pitchDeck ? formData.pitchDeck.name : 'Pilih file PDF (wajib)...'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Business Description */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-400" />
                Deskripsi Bisnis <span className="text-red-400 text-base">* (min. 100 karakter)</span>
              </h3>
              <textarea
                required
                rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary-500 resize-none"
                placeholder="Ceritakan tentang bisnis Anda: produk/jasa, target pasar, unique value proposition, traction saat ini, dll."
                value={formData.businessDescription}
                onChange={(e) => setFormData({...formData, businessDescription: e.target.value})}
              />
            </div>

            {/* NDA Agreement Checkbox */}
            <div className="mb-8 p-6 bg-white/5 border border-primary-500/30 rounded-xl">
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  id="ndaAgreement"
                  required
                  className="w-6 h-6 mt-0.5 rounded border-white/20 bg-white/5 text-primary-600 focus:ring-primary-500 cursor-pointer flex-shrink-0"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({...formData, agreeToTerms: e.target.checked})}
                />
                <label htmlFor="ndaAgreement" className="text-white/70 text-sm cursor-pointer select-none">
                  <span className="text-red-400">*</span> Saya setuju dengan{' '}
                  <button
                    type="button"
                    onClick={() => setShowNdaModal(true)}
                    className="text-primary-400 underline hover:text-primary-300 transition-colors"
                  >
                    syarat dan ketentuan NDA
                  </button>
                  {' '}dan memberikan izin kepada Emporium Solution untuk mengakses dokumen-dokumen yang saya unggah untuk keperluan analisis dan matching investor.
                </label>
              </div>
            </div>

            {/* Submit */}
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 p-4 bg-green-600/20 border border-green-500/50 rounded-xl text-center"
              >
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Formulir berhasil dikirim! Tim kami akan menghubungi Anda dalam 1-2 hari kerja.</span>
                </div>
              </motion.div>
            )}
            
            {submitStatus === 'error' && errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 p-4 bg-red-600/20 border border-red-500/50 rounded-xl text-center"
              >
                <div className="flex items-center justify-center gap-2 text-red-400">
                  <span className="font-semibold">{errorMessage}</span>
                </div>
              </motion.div>
            )}
            
            <div className="text-center mb-10">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Mengirim...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Submit Aplikasi
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
              <p className="text-white/50 text-sm mt-4">
                *Tim kami akan menghubungi Anda dalam 1-2 hari kerja
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/10 mb-10" />

            {/* Contact Help */}
            <div className="text-center">
              <p className="text-white/60 mb-4">Butuh bantuan atau punya pertanyaan?</p>
              <motion.a
                href={`tel:${companyInfo.phone}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Hubungi Kami
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.form>
        </div>
      </section>

      {/* NDA Modal */}
      <NdaModal
        isOpen={showNdaModal}
        onClose={() => setShowNdaModal(false)}
        onAgree={() => {
          setFormData({...formData, agreeToTerms: true});
          setShowNdaModal(false);
        }}
      />

    </div>
  );
}
